import React from 'react'
import { X, FileSignature, Eye, Download } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { TEMPLATE_REGISTRY } from '../../services/esign/templates'
import { useSignatureProvider } from '../../services/esign/useSignatureProvider'
import type { SigningSurfaceProps } from '../../services/esign/providers/types'
import type {
  DocumentType,
  DocumentRenderData,
  ExchangerData,
  BuyerData,
  SellerData,
  ClosingCompanyData,
  PropertyData,
  DisbursementData,
  IdentificationData,
  ExchangeBundleMeta,
  EscrowWireData,
  CounterSignatureData,
} from '../../services/esign/types'

export type { IdentifiedPropertyForDoc as IdentifiedPropertyForModal } from '../../services/esign/types'
export type { IdentificationData } from '../../services/esign/types'
export type { DocumentType } from '../../services/esign/types'

interface DocumentSigningModalProps {
  documentName: string
  documentType: DocumentType
  exchangerData: ExchangerData
  buyerData?: BuyerData
  sellerData?: SellerData
  closingCompanyData?: ClosingCompanyData
  propertyData?: PropertyData
  disbursementData?: DisbursementData
  identificationData?: IdentificationData
  bundleMeta?: ExchangeBundleMeta
  escrowData?: EscrowWireData
  counterSignatures?: CounterSignatureData
  signedSignature?: DocumentRenderData['signature']
  signerEmail?: string
  exchangeId?: string
  onSign: (signature: string) => void
  onClose: () => void
  viewOnly?: boolean
}

export const DocumentSigningModal: React.FC<DocumentSigningModalProps> = ({
  documentName, documentType, exchangerData, buyerData, sellerData, closingCompanyData,
  propertyData, disbursementData, identificationData, bundleMeta, escrowData, counterSignatures,
  signedSignature, signerEmail = '', exchangeId = '',
  onSign, onClose, viewOnly = false,
}) => {
  useBodyScrollLock(true)
  const provider = useSignatureProvider()

  const renderData: DocumentRenderData = {
    exchanger: exchangerData,
    buyer: buyerData,
    seller: sellerData,
    closingCompany: closingCompanyData,
    property: propertyData,
    disbursement: disbursementData,
    identification: identificationData,
    bundleMeta,
    escrow: escrowData,
    counterSignatures,
    signature: signedSignature,
  }

  const template = TEMPLATE_REGISTRY[documentType]

  const surfaceProps: SigningSurfaceProps = {
    request: {
      documentType,
      documentName,
      renderData,
      signerName: exchangerData.names,
      signerEmail,
      exchangeId,
    },
    onComplete: (result) => onSign(result.signatureValue),
    onCancel: onClose,
  }

  const footer = !viewOnly ? provider.renderFooter?.(surfaceProps) : null

  return (
    <div className="modal-overlay modal-overlay-nested">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between bg-card">
          <div className="flex items-center space-x-3">
            {viewOnly ? (
              <><Eye className="h-6 w-6 text-primary-600 dark:text-primary-400" /><h2 className="text-xl font-semibold text-foreground">View {documentName}</h2></>
            ) : (
              <><FileSignature className="h-6 w-6 text-primary-600 dark:text-primary-400" /><h2 className="text-xl font-semibold text-foreground">Sign {documentName}</h2></>
            )}
          </div>
          <div className="flex items-center gap-2">
            {template.sourceDocxUrl && (
              <a
                href={template.sourceDocxUrl}
                download
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border hover:bg-muted rounded-md px-3 py-1.5 transition-colors"
                aria-label={`Download ${template.sourceDocxLabel ?? 'original template'}`}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Download original</span>
              </a>
            )}
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors" aria-label="Close">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-scroll pr-2 min-h-0 p-6 space-y-6" style={{ maxHeight: '100%' }}>
          <Card className="p-6 bg-muted/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Document Preview</h3>
            {template.render(renderData)}
          </Card>
          {!viewOnly && provider.renderSigningSurface(surfaceProps)}
        </div>
        {footer && (
          <div className="border-t border-border bg-card">
            {footer}
          </div>
        )}
        {viewOnly && (
          <div className="border-t border-border px-6 py-4 bg-muted/50">
            <div className="flex items-center justify-between">
              {template.sourceDocxUrl ? (
                <a
                  href={template.sourceDocxUrl}
                  download
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:bg-muted rounded-md px-3 py-2 transition-colors"
                  aria-label={`Download ${template.sourceDocxLabel ?? 'original template'}`}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download original
                </a>
              ) : (
                <span />
              )}
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
